import { Injectable } from "@angular/core";
import { BfxBaseBlotterService } from "../../bfx-base-blotter/bfx-base-blotter.service";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  OpsIntPromiseLookupService,
  OpsIntTableDefinitionFactory,
  ProfileSettingKeys,
  TableDefinition,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  SysFeatures,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxBlotterFilterService } from "../../bfx-blotter-filter/bfx-blotter-filter.service";

import { BfxBlotterFilter } from "../../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BlotterType } from "shared/cubiq-model-multi-asset";
import { BfxBlotterOpsIntFilterService } from "./bfx-blotter-ops-int-filter.service";
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";
import { Subject, Subscription } from "rxjs";
import { BfxUserProfileService } from "../../bfx-common/services/bfx-user-profile.service";
import {
  GLComponentAlertStatus,
  GoldenLayoutServiceProfileConfig,
} from "app/golden-layout/golden-layout-component";
import { OpsIntBlotterFilterListService } from "./bfx-blotter-ops-int-filter-list.service";
import { auditTime, throttleTime } from "rxjs/operators";
// import { BfxCommsOpsIntService } from "./bfx-comms-ops-int.service";

const OPS_INT_AUTO_OPEN_INTERVAL = 300;
const OPS_INT_ERROR_AUDIT_TIME = 100;

export const BLOTTER_EVENT_TIME_FIELD = "blotter_add_time";
@Injectable()
export class BfxBlotterOpsIntService
  extends BfxBaseBlotterService
  implements GoldenLayoutServiceProfileConfig
{
  private filterConfiguration: string[] = [];
  private filters: Array<BfxBlotterFilter> = [];
  private selectedFilters: Array<BfxBlotterFilter> = [];
  private modelObjectPresent: any = {};
  private component: any = null;

  private areBenchmarksEnabled: boolean = true;

  public subjectNewItems: Subject<void> = new Subject<any>();
  public subjectUpdatedItems: Subject<string[]> = new Subject<any>();
  public openOpsIntSubscription: Subscription;
  public selectItemSubject: Subject<string> = new Subject<string>();
  public selectItemSubscription: Subscription;

  private readonly profileKey = `${ProfileSettingKeys.userProfileDefaultBlotter}/${
    BlotterType[BlotterType.OPSINTERVENTION]
  }/SERVICE`;

  /** Highest event ID stored in this service?  */
  private maxEventId: number = 0;

  /** Highest ID marked as view in the blotter?  */
  private maxViewedId: number = 0;

  constructor(
    public dataService: OpsIntPromiseLookupService,
    public tableDefinitionFactory: OpsIntTableDefinitionFactory,
    public profileActionService: ProfilePromiseActionService,
    public opsIntFilterService: BfxBlotterOpsIntFilterService,
    public goldenLayoutService: GoldenLayoutService,
    protected filterService: BfxBlotterFilterService,
    protected userProfileService: BfxUserProfileService,
    protected opsIntBlotterFilterList: OpsIntBlotterFilterListService,
    protected systemConfigService: SystemConfigPromiseService, // protected commsOpsService: BfxCommsOpsIntService,
  ) {
    super(dataService, tableDefinitionFactory);
    this.filterService.modelUpdateSubject.subscribe(this.refreshFilters.bind(this));
    this.opsIntFilterService.updateFilterDataCallback = this.updateFilterData.bind(this);
    // reduce opening inbox to a frequency on a human scale
    // to reduce pressure on golden-layout/openfin logic during large acceptance bursts
    this.openOpsIntSubscription = this.subjectNewItems
      .pipe(throttleTime(OPS_INT_AUTO_OPEN_INTERVAL))
      .subscribe(() => {
        this.openBlotterOpsInt();
      });
  }

  protected subscribeToSubject(): void {
    super.subscribeToSubject();

    let updateErrorSubject = new Subject<void>();
    let pendingErrorUpdates = new Map<string, CubiqTS.OpsIntEventImpl>();

    this.dataService
      .getModelErrorSubject()
      .asObservable()
      .subscribe((event: CubiqTS.OpsIntEventImpl) => {
        let id = event.eventId.toString();
        let opsIntEvent: CubiqTS.OpsIntEventImpl = Object.assign(
          new CubiqTS.OpsIntEventImpl(),
          this._rawData[id],
        );
        opsIntEvent.opsErrors = event.opsErrors;
        pendingErrorUpdates.set(id, opsIntEvent);
        updateErrorSubject.next();
      });

    updateErrorSubject.pipe(auditTime(OPS_INT_ERROR_AUDIT_TIME)).subscribe(() => {
      let internalErrorUpdates = Array.from(pendingErrorUpdates.values());
      pendingErrorUpdates.clear();
      this.onFullUpdateEvent(internalErrorUpdates);
    });
  }

  protected refreshFilters(): Promise<void> {
    return new Promise<void>((res, rej) => {
      this.systemConfigService
        .getSystemConfig(SysFeatures.benchmarksEnabled)
        .then((areBenchmarksEnabled) => {
          this.areBenchmarksEnabled = areBenchmarksEnabled.toUpperCase() == "Y";
          this.filters = OpsIntBlotterFilterListService.getAllCategoryFilters(
            this.opsIntBlotterFilterList,
          );
          res();
        });
    });
  }

  protected resolvePriming(res, rej): void {
    this.refreshFilters().then(() => {
      this.tableDefinitionFactory.defineTable().then((tableDefinition: TableDefinition) => {
        this.goldenLayoutService.getProfileConfiguration().then((config) => {
          // newly created users, the filters are enabled by default
          if (
            config == null ||
            config.serviceProfileConfig == null ||
            config.serviceProfileConfig["bfx-blotter-ops-int"] == null ||
            config.serviceProfileConfig["bfx-blotter-ops-int"].service == null
          ) {
            this.filters.forEach((filter) => this.filterConfiguration.push(filter.filterId));
          } else {
            this.checkSelectedFiltersArePristine(
              config.serviceProfileConfig["bfx-blotter-ops-int"].service,
            );
            this.updateFilterSelection(this.filterConfiguration);
          }
          res();

          if (this.getItemCount() > 0) {
            setTimeout(this.openBlotterOpsInt.bind(this), 4500);
          }
        });
      });
    });
  }

  protected openBlotterOpsInt(): void {
    this.goldenLayoutService.showOpsInt(true);
  }

  public updateFilterSelection(filter: Array<string>): void {
    this.filterConfiguration = filter;

    this.selectedFilters = this.filters.filter(
      (fil: BfxBlotterFilter) => this.filterConfiguration.indexOf(fil.filterId) >= 0,
    );
    this.opsIntFilterService.setSelectedFilters(this.selectedFilters);
    this.updateFilterData();
  }

  public updateFilterData(): void {
    this.modelObjectPresent = {};
    Object.keys(this._rawData).forEach((key: any) => {
      if (this.doesExternalFilterPass(this._rawData[key])) {
        this.modelObjectPresent[key] = true;
      }
    });

    if (this.component != null) {
      this.updateBadgeCount(this.component);
    }
  }
  public doesExternalFilterPass(node: any): boolean {
    try {
      let eventCategory = this.opsIntFilterService.checkEventCategory(node);
      let isBmarkCategory =
        eventCategory == this.opsIntBlotterFilterList.getCategoryId("Benchmarks");
      if (!this.areBenchmarksEnabled && isBmarkCategory) {
        return true;
      }
      let isCategorizedEvent = eventCategory != null;

      if (isCategorizedEvent) {
        if (this.selectedFilters != null && this.selectedFilters.length > 0) {
          return this.opsIntFilterService.doFiltersPass(node);
        } else {
          return false;
        }
      }
    } catch (error) {
      logger.out.error("Error while doing a filter pass.", error);
    }
    return true;
  }

  public getItemCount(): number {
    return Object.keys(this.modelObjectPresent).length;
  }

  protected override addOrUpdateRow(row: any): any {
    if (row) {
      const now = new Date().getTime();
      row[BLOTTER_EVENT_TIME_FIELD] = now;
      this.maxEventId = now;
    }
    super.addOrUpdateRow(row);
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    super.onFullUpdateEvent(rows);

    // OMS-33523 multiline doesn't update even with
    // super.onFullUpdateEvent(rows, true);

    let hasNewRows: boolean = false;

    let updatedItemIds: Array<string> = [];

    rows.forEach((row: any) => {
      let eventId: string = row[this._tableDefinition.primaryKey];
      let wasPresent: boolean = this.modelObjectPresent[eventId];
      let isPresent: boolean = this.doesExternalFilterPass(row);

      if (!wasPresent && isPresent) {
        hasNewRows = true;
      }

      if (wasPresent) {
        updatedItemIds.push(eventId);
      }

      if (isPresent) {
        this.modelObjectPresent[eventId] = true;
      } else if (!isPresent) {
        delete this.modelObjectPresent[eventId];
      }
    });

    if (updatedItemIds.length > 0) {
      this.subjectUpdatedItems.next(updatedItemIds);
    }

    if (hasNewRows) {
      this.subjectNewItems.next();
    }
  }

  protected onDeleteEvent(eventId: string): void {
    super.onDeleteEvent(eventId);
    delete this.modelObjectPresent[eventId];
  }

  public updateBadgeCount(component: any): void {
    this._ready.then(() => {
      if (this.component == null && component != null) {
        this.component = component;
        this.updateFilterData();
        return;
      }
      let badgeCount = this.getItemCount();
      let alertStatus = this.component.getAlertStatus();
      this.goldenLayoutService.updateBadge(component, badgeCount, alertStatus);
    });
  }

  public getFilterConfiguration(): string[] {
    return this.filterConfiguration;
  }

  public glServiceProfileConfig(): GoldenLayoutServiceProfileConfig {
    return this;
  }

  public getSelectedFilters(): Array<BfxBlotterFilter> {
    return this.selectedFilters;
  }

  public getServiceConfiguration(): any {
    let config: any = {};

    if (this.selectedFilters != null) {
      config.selectedFilters = this.selectedFilters.map(
        (filter: BfxBlotterFilter) => filter.filterId,
      );
    }

    return config;
  }

  public setServiceConfiguration(config: any): void {
    if (typeof config == "object" && config != null) {
      if (config.selectedFilters != null && Array.isArray(config.selectedFilters)) {
        this.checkSelectedFiltersArePristine(config);
        this.updateFilterSelection(this.filterConfiguration);
      }
    }
  }

  private checkSelectedFiltersArePristine(serviceProfileConfig: any) {
    const selectedFilters: [] = Array.isArray(serviceProfileConfig.selectedFilters)
      ? serviceProfileConfig.selectedFilters
      : [];
    let filterIdList: string[] = this.filters.map((filter) => filter.filterId);
    // check in the meantime something was disabled
    let areSelectedFiltersPristine: boolean = selectedFilters.every((filterId) =>
      filterIdList.includes(filterId),
    );
    if (selectedFilters.length != 0 && areSelectedFiltersPristine) {
      this.filterConfiguration = selectedFilters;
    } else {
      this.filters.forEach((filter) => this.filterConfiguration.push(filter.filterId));
    }
  }

  /**
   * The highest event ID stored in service
   * @returns
   */
  public getMaxEventId(): number {
    return this.maxEventId;
  }

  /**
   * The highest event ID marked as read - used for restoring view
   * @returns
   */
  public getMaxViewedId(): number {
    return this.maxViewedId;
  }

  public updateMaxViewedId(): void {
    this.maxViewedId = this.maxEventId;
  }
}
